import React, { useEffect, useState, useRef } from 'react';
import { FadeIn } from './FadeIn';
import { BellIcon, PackageIcon, AlertTriangleIcon } from 'lucide-react';
function useInView(threshold = 0.3) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, {
      threshold
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return {
    ref,
    visible
  };
}
function BrandPills() {
  const {
    ref,
    visible
  } = useInView();
  const brands = [{
    name: 'TikTok',
    bg: 'bg-[#010101]',
    text: 'text-white'
  }, {
    name: 'Amazon',
    bg: 'bg-[#FF9900]',
    text: 'text-[#232F3E]'
  }, {
    name: 'Target',
    bg: 'bg-[#CC0000]',
    text: 'text-white'
  }, {
    name: 'Temu',
    bg: 'bg-[#F05A28]',
    text: 'text-white'
  }, {
    name: 'Shein',
    bg: 'bg-[#222]',
    text: 'text-white'
  }];
  return <div ref={ref} className="flex justify-between mt-2 md:mt-4" data-id="element-272">
      {brands.map((b, i) => <span key={b.name} className={`${b.bg} ${b.text} px-2.5 md:px-4 py-1 md:py-2 rounded-full text-[11px] md:text-base font-medium transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`} style={{
      transitionDelay: `${i * 200}ms`
    }} data-id="element-273">
          {b.name}
        </span>)}
    </div>;
}
// Custom icons for cart items
function SweaterIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gold" data-id="element-274">
      <path d="M6 2L2 8l4 2v12h12V10l4-2-4-6" data-id="element-275" />
      <path d="M12 2v6" data-id="element-276" />
    </svg>;
}
function SkincareIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gold" data-id="element-277">
      <path d="M8 2h8l2 6H6l2-6Z" data-id="element-278" />
      <rect x="6" y="8" width="12" height="14" rx="2" data-id="element-279" />
    </svg>;
}
function CandleIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gold" data-id="element-280">
      <rect x="8" y="10" width="8" height="12" rx="1" data-id="element-281" />
      <path d="M12 10V7" data-id="element-282" />
      <path d="M10 7c0-2 2-4 2-4s2 2 2 4" data-id="element-283" />
    </svg>;
}
function PhoneNotification() {
  const {
    ref,
    visible
  } = useInView();
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setPulse(true), 1200);
      return () => clearTimeout(t);
    }
  }, [visible]);
  return <div ref={ref} className={`my-3 md:my-5 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-284">
      <div className={`bg-white rounded-2xl shadow-lg border border-red-200 p-3 max-w-[300px] mx-auto relative overflow-hidden ${pulse ? 'animate-pulse-once' : ''}`} data-id="element-285">
        <div className="absolute top-0 left-0 w-1 h-full bg-red-400" data-id="element-286"></div>
        <div className="flex items-start gap-2.5 pl-1" data-id="element-287">
          <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-288">
            <AlertTriangleIcon size={16} className="text-red-500" data-id="element-289" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-290">
            <div className="flex items-center justify-between" data-id="element-291">
              <span className="text-[10px] font-bold text-red-600 uppercase tracking-wide" data-id="element-292">
                Bank Alert
              </span>
              <span className="text-[9px] text-charcoal/40" data-id="element-293">just now</span>
            </div>
            <p className="text-xs text-charcoal/80 mt-0.5 leading-snug" data-id="element-294">
              Purchase of <span className="font-bold text-red-600" data-id="element-295">$78.00</span>{' '}
              on your card ending •••4821
            </p>
            <p className="text-[10px] text-charcoal/40 mt-0.5" data-id="element-296">
              Available balance: $127.43
            </p>
          </div>
        </div>
      </div>
    </div>;
}
function CartVisual() {
  const {
    ref,
    visible
  } = useInView();
  const items = [{
    name: 'Sweater',
    price: '$38',
    icon: <SweaterIcon data-id="element-297" />
  }, {
    name: 'Skincare set',
    price: '$24',
    icon: <SkincareIcon data-id="element-298" />
  }, {
    name: 'Candle',
    price: '$16',
    icon: <CandleIcon data-id="element-299" />
  }];
  return <div ref={ref} className="my-2 md:my-4" data-id="element-300">
      <div className="bg-white/80 rounded-xl border border-gold/20 overflow-hidden max-w-[280px]" data-id="element-301">
        {items.map((item, i) => <div key={item.name} className={`flex items-center justify-between px-3.5 py-2 border-b border-gold/10 last:border-0 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`} style={{
        transitionDelay: `${i * 150}ms`
      }} data-id="element-302">
            <div className="flex items-center gap-2" data-id="element-303">
              {item.icon}
              <span className="text-sm text-charcoal/80" data-id="element-304">{item.name}</span>
            </div>
            <span className="text-sm font-medium text-navy" data-id="element-305">{item.price}</span>
          </div>)}
      </div>
    </div>;
}
function TriggerTags() {
  const {
    ref,
    visible
  } = useInView();
  const triggers = ['Stress', 'Boredom', 'A sale', 'An ad'];
  return <div ref={ref} className="flex justify-between mt-2" data-id="element-306">
      {triggers.map((t, i) => <span key={t} className={`bg-navy/8 text-navy border border-navy/15 px-3 md:px-4 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-all duration-600 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`} style={{
      transitionDelay: `${i * 150}ms`
    }} data-id="element-307">
          {t}
        </span>)}
    </div>;
}
function PackageStack() {
  const {
    ref,
    visible
  } = useInView();
  const items = ['Clothes', 'Beauty products', 'Random impulse items'];
  return <div ref={ref} className="mt-1.5 space-y-1" data-id="element-308">
      {items.map((item, i) => <div key={item} className={`flex items-center gap-2 bg-cream border border-gold/20 rounded-lg px-3 py-1.5 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`} style={{
      transitionDelay: `${i * 120}ms`
    }} data-id="element-309">
          <PackageIcon size={13} className="text-gold flex-shrink-0" data-id="element-310" />
          <span className="text-sm text-charcoal/80" data-id="element-311">{item}</span>
        </div>)}
    </div>;
}
export function StorySection() {
  return <section className="py-4 md:py-10 px-4 md:px-6" data-id="element-312">
      <div className="max-w-[640px] mx-auto space-y-1 md:space-y-2.5 text-sm md:text-base text-charcoal/90 font-light" data-id="element-313">
        <FadeIn data-id="element-314">
          <h2 className="font-serif text-xl md:text-2xl text-navy mb-2 md:mb-5 text-center" data-id="element-315">
            Have You Ever Noticed This?
          </h2>
        </FadeIn>

        <FadeIn data-id="element-316">
          <p data-id="element-317">Shopping can feel like therapy…</p>
          <p className="mt-1" data-id="element-318">until the bank notification arrives.</p>
        </FadeIn>

        <FadeIn data-id="element-319">
          <div className="mt-2 md:mt-3 space-y-0.5" data-id="element-320">
            <p data-id="element-321">You're tired. The day was stressful.</p>
            <p data-id="element-322">So you open your phone and start scrolling.</p>
          </div>
        </FadeIn>

        <FadeIn data-id="element-323">
          <BrandPills data-id="element-324" />
        </FadeIn>

        <FadeIn data-id="element-325">
          <p className="mt-2 md:mt-3" data-id="element-326">At first you're just browsing.</p>
          <p className="mt-0.5" data-id="element-327">Then something catches your attention.</p>
        </FadeIn>

        <FadeIn data-id="element-328">
          <CartVisual data-id="element-329" />
          <p className="mt-1 text-sm text-charcoal/60 italic" data-id="element-330">
            You add it to your cart. Nothing crazy.
          </p>
        </FadeIn>

        <FadeIn data-id="element-331">
          <p className="mt-2 md:mt-3" data-id="element-332">It feels harmless. Even comforting.</p>
          <p className="mt-0.5" data-id="element-333">
            Almost like a little reward for getting through the day.
          </p>
        </FadeIn>

        <FadeIn data-id="element-334">
          <p className="mt-3 md:mt-5" data-id="element-335">
            Then the next morning your phone lights up.
          </p>
        </FadeIn>

        <PhoneNotification data-id="element-336" />

        <FadeIn data-id="element-337">
          <p data-id="element-338">And suddenly that good feeling disappears.</p>
          <p className="mt-1" data-id="element-339">A quiet thought crosses your mind:</p>
          <p className="font-serif text-base md:text-lg text-navy font-bold my-2 text-center italic" data-id="element-340">
            "Why do I keep doing this?"
          </p>
        </FadeIn>

        <FadeIn data-id="element-341">
          <p className="mt-2.5 md:mt-4" data-id="element-342">Because here's the strange part.</p>
          <p className="mt-0.5" data-id="element-343">You're not irresponsible.</p>
          <p className="mt-0.5" data-id="element-344">
            In fact, in most areas of your life you're extremely capable.
          </p>
        </FadeIn>

        <FadeIn data-id="element-345">
          <div className="mt-1.5 md:mt-2 space-y-0.5" data-id="element-346">
            <p data-id="element-347">You work hard. You're self-aware.</p>
            <p data-id="element-348">You're always trying to improve yourself.</p>
          </div>
        </FadeIn>

        <FadeIn data-id="element-349">
          <p className="mt-2 md:mt-3 font-medium text-navy" data-id="element-350">
            But when it comes to money…
          </p>
          <p className="mt-0.5" data-id="element-351">something feels out of control.</p>
        </FadeIn>

        <FadeIn data-id="element-352">
          <p className="mt-2 md:mt-3" data-id="element-353">
            And that gap between who you believe you are and how you spend
            creates something painful.{' '}
            <span className="font-serif font-bold text-navy text-base md:text-lg" data-id="element-354">
              Shame.
            </span>
          </p>
        </FadeIn>

        <FadeIn data-id="element-355">
          <h2 className="font-serif text-xl md:text-2xl text-navy mt-4 md:mt-7 mb-2 md:mb-4 text-center" data-id="element-356">
            The Quiet Financial Anxiety Nobody Talks About
          </h2>
        </FadeIn>

        <FadeIn data-id="element-357">
          <p data-id="element-358">
            You make decent money. Maybe somewhere between $50k and $90k a year.
          </p>
          <p className="mt-0.5" data-id="element-359">You should feel financially stable.</p>
        </FadeIn>

        <FadeIn data-id="element-360">
          <p className="mt-2 md:mt-3" data-id="element-361">
            But sometimes opening your banking app still makes you nervous.
          </p>
          <p className="mt-0.5" data-id="element-362">So you delay checking it.</p>
        </FadeIn>

        <FadeIn data-id="element-363">
          <p className="mt-2 md:mt-3" data-id="element-364">Packages arrive at your door.</p>
          <PackageStack data-id="element-365" />
        </FadeIn>

        <FadeIn data-id="element-366">
          <p className="mt-2 md:mt-3" data-id="element-367">
            Some get used. Some sit untouched in your closet.
          </p>
        </FadeIn>

        <FadeIn data-id="element-368">
          <p className="mt-2.5 md:mt-4" data-id="element-369">Every month you tell yourself:</p>
          <p className="font-serif text-base md:text-lg text-navy font-bold my-1.5 text-center italic" data-id="element-370">
            "This month will be different."
          </p>
          <p data-id="element-371">And you mean it.</p>
        </FadeIn>

        <FadeIn data-id="element-372">
          <p className="mt-2 md:mt-3" data-id="element-373">But then something happens.</p>
          <TriggerTags data-id="element-374" />
        </FadeIn>

        <FadeIn data-id="element-375">
          <p className="mt-2.5 md:mt-4 font-medium" data-id="element-376">
            And slowly… the cycle repeats.
          </p>
        </FadeIn>
      </div>
    </section>;
}